// ═══════════════════════════════════════════════════════════════════════════
// MATCHPROP store-side resolution seams (@codex P2-A/P2-C #420)
// ═══════════════════════════════════════════════════════════════════════════
//
// Builds the `MatchpropResolutionDeps` the store wiring
// (`storeHelpers.resolveGeometryDocumentCommand`) injects into the pure
// `resolveMatchpropApplyToPropertiesApply` helper. Extracted from `storeHelpers`
// (file-size policy) — it depends on `dxfGeometry` (one-truth preview builder)
// and `propertyTranslator` (document-persistence predicate), both already in
// `storeHelpers`' dependency graph, so no new cycle is introduced.

import type {
  DimensionStyle,
  DrawingDocument,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import type { EditorShellState, EntityPreview } from "@dxf-ai-editor/contracts";

import { buildShellEntityPreview } from "./dxfGeometry";
import { isLabelDocumentPersistable } from "./propertyTranslator";
import type { MatchpropResolutionDeps } from "./matchpropResolution";

type CacheLookup =
  | ((spaceId: string) => readonly DrawingEntity[] | undefined)
  | null;

/**
 * The single MATCHPROP shell↔document resolution shape (@codex #420 round-3).
 *
 * `fromShell` is the load-bearing discriminator for the "visible shell preview
 * if present, else document" coherence rule that ALL of source-read, undo-
 * snapshot, and target-write converge on:
 *   • `fromShell:true`  → the entity IS in the reactive `state.drawing.entities`
 *     shell; the `preview` reflects any un-persisted shell-only edit (e.g. a
 *     `chprop/apply` that never reached the document), so it is the VISIBLE
 *     truth. `documentEntity` (if any) is a possibly-STALE snapshot — callers
 *     must NOT read a property from it in this case (@codex P2-B).
 *   • `fromShell:false` → the shell lacks the entity (large / streamed import
 *     above `SHELL_ENTITY_PREVIEW_THRESHOLD`); the `preview` is synthesised
 *     from `documentEntity` via the one-truth `buildDocumentBackedPreview`, and
 *     `documentEntity` is the authoritative (lossless) source to read from.
 */
export interface CoherentEntityResolution {
  preview: EntityPreview;
  documentEntity?: DrawingEntity;
  fromShell: boolean;
}

/**
 * @codex P2-C (#420) — locate a DOCUMENT entity by id, cache-aware. On large
 * imports the reactive `space.entities` is stashed to the non-reactive cache
 * (the SAME fallback `buildShellEntityPreviewsFromGeometryDocument` uses), so a
 * plain `space.entities.find` would miss it and the MATCHPROP source resolution
 * would no-op. One-truth reader for both the source preview build and the
 * lossless source-colour read in `resolveMatchpropApplyToPropertiesApply`.
 */
function findGeometryDocumentEntity(
  document: DrawingDocument | undefined,
  entityId: string,
  cacheLookup: CacheLookup,
): DrawingEntity | undefined {
  if (!document) return undefined;
  for (const space of document.spaces) {
    const source =
      space.entities.length > 0
        ? space.entities
        : cacheLookup?.(space.id) ?? [];
    const hit = source.find((e) => e.id === entityId);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * ONE-TRUTH document→preview builder (@codex #420 round-3). Builds the shell
 * `EntityPreview` for a typed document entity with the document's style tables,
 * using the same `buildShellEntityPreview` the import flow uses. Shared by the
 * source-read + undo-snapshot resolver (`resolveCoherentEntityPreview`) AND the
 * reducer's on-demand target-preview synthesis, so every large-import path
 * projects the document identically.
 */
export function buildDocumentBackedPreview(
  documentEntity: DrawingEntity,
  document: DrawingDocument,
): EntityPreview {
  const dimStylesByName = new Map<string, DimensionStyle>();
  for (const s of document.styleTables?.dimensionStyles ?? []) {
    dimStylesByName.set(s.name, s);
  }
  return buildShellEntityPreview(
    documentEntity,
    dimStylesByName,
    document.blockRecords ?? {},
    document.blocks ?? {},
    document.insunits ?? 0,
  );
}

/**
 * THE coherent shell↔document entity resolver (@codex #420 round-3) — the single
 * choke-point every MATCHPROP / properties-apply lookup routes through so the
 * "visible shell preview if present, else document" rule has ONE implementation.
 *
 * Used by:
 *   • source-read (`buildMatchpropResolutionDeps.resolveSourceEntity`), so the
 *     copied SOURCE reflects un-persisted shell edits (@codex P2-B) and still
 *     resolves above the shell-preview threshold (round-2 P2-C);
 *   • undo-snapshot (`editorStore` inverse `prevById` / `nextById`), so the undo
 *     inverse is populated from the PRE / POST document when the shell is empty
 *     on large imports (@codex P2-A).
 *
 * `shellLookup` returns the reactive shell preview for the id (or `undefined`);
 * callers that only want the document projection pass `() => undefined`.
 */
export function resolveCoherentEntityPreview(
  entityId: string,
  shellLookup: (id: string) => EntityPreview | undefined,
  document: DrawingDocument | undefined,
  cacheLookup: CacheLookup,
): CoherentEntityResolution | null {
  const shellHit = shellLookup(entityId);
  const documentEntity = findGeometryDocumentEntity(
    document,
    entityId,
    cacheLookup,
  );
  if (shellHit) {
    // Shell present → the VISIBLE preview wins; `documentEntity` is threaded for
    // completeness but callers keyed on `fromShell` must NOT read a (possibly
    // stale) property from it here (@codex P2-B).
    return { preview: shellHit, documentEntity, fromShell: true };
  }
  if (!documentEntity || !document) return null;
  return {
    preview: buildDocumentBackedPreview(documentEntity, document),
    documentEntity,
    fromShell: false,
  };
}

/**
 * Assemble the shared MATCHPROP resolution seams used by BOTH the typed
 * `matchprop/apply` router AND the CLI `MATCHPROP` text arm:
 *   • P2-C source resolver — resolve the armed source from shell-or-DOCUMENT so
 *     matchprop works above `SHELL_ENTITY_PREVIEW_THRESHOLD` (empty
 *     `state.drawing.entities`). Falls back to the document entity (reactive
 *     spaces or the non-reactive cache) and builds its preview with the
 *     ONE-TRUTH `buildShellEntityPreview`, also returning the typed entity for a
 *     lossless colour read.
 *   • P2-A persistence gate — drop labels the document translator cannot persist
 *     (viewport special etc.) so the resolved command never advertises a no-op
 *     copy.
 */
export function buildMatchpropResolutionDeps(
  state: EditorShellState,
  geometryDocument: DrawingDocument | undefined,
  cacheLookup: CacheLookup,
): MatchpropResolutionDeps {
  return {
    // @codex P2-B/P2-C (#420) — resolve the armed source through the ONE
    // coherent shell↔document resolver: the visible shell preview wins when the
    // source is in `state.drawing.entities` (reflects un-persisted shell edits),
    // else fall back to the document (large / streamed import). The `fromShell`
    // flag it returns is what the pure resolution helper keys the lossless
    // document-colour read on.
    resolveSourceEntity: (entityId: string) =>
      resolveCoherentEntityPreview(
        entityId,
        (id) => state.drawing.entities.find((e) => e.id === id),
        geometryDocument,
        cacheLookup,
      ),
    isLabelDocumentPersistable,
  };
}
